import { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://lvexgzeavcpzmaehzzyz.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imx2ZXhnemVhdmNwem1hZWh6enl6Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY3MzIxOTksImV4cCI6MjA5MjMwODE5OX0.8_5vagDfQ8Cln_vEbXP7WklaWzPwelLqym6ilx9jGIA";
const sb = createClient(SUPABASE_URL, SUPABASE_KEY);

const DEFAULT_TABLES = Array.from({ length: 10 }, (_, i) => ({
  id: `table-${i + 1}`,
  name: `Table ${i + 1}`,
  capacity: 8,
  guests: [],
}));

const initialState = { guests: [], tables: DEFAULT_TABLES, nextId: 1 };

// ── Supabase helpers ───────────────────────────────────────────────────────
async function loadFromSupabase() {
  const [{ data: guests }, { data: tables }] = await Promise.all([
    sb.from("guests").select("*").order("id"),
    sb.from("tables").select("*").order("name"),
  ]);
  const mappedGuests = (guests || []).map(g => ({
    id: g.id, name: g.name, partnerName: g.partner_name || "",
    list: g.list, group: g.group_name, dietary: g.dietary || "None",
    partnerDietary: g.partner_dietary || "None", rsvp: g.rsvp || "pending",
    notes: g.notes || "", invited: g.invited,
    address: g.address || "", email: g.email || "",
  }));
  const mappedTables = (tables || []).map(t => ({
    id: t.id, name: t.name, capacity: t.capacity, guests: t.guests || [],
  }));
  const nextId = mappedGuests.length > 0 ? Math.max(...mappedGuests.map(g => g.id)) + 1 : 1;
  return {
    guests: mappedGuests,
    tables: mappedTables.length > 0 ? mappedTables : DEFAULT_TABLES,
    nextId,
  };
}

async function upsertGuest(g) {
  await sb.from("guests").upsert({
    id: g.id, name: g.name, partner_name: g.partnerName || null,
    list: g.list, group_name: g.group, dietary: g.dietary,
    partner_dietary: g.partnerDietary, rsvp: g.rsvp,
    notes: g.notes || null, invited: g.invited,
    address: g.address || null, email: g.email || null,
  });
}

async function deleteGuest(id) {
  await sb.from("guests").delete().eq("id", id);
}

async function upsertTable(t) {
  await sb.from("tables").upsert({
    id: t.id, name: t.name, capacity: t.capacity, guests: t.guests,
  });
}

const GROUPS = ["Family", "Friends", "Work", "Partner's Family", "Partner's Friends", "Other"];
const DIETARY = ["None", "Vegetarian", "Vegan", "Gluten-Free", "Halal", "Kosher", "Other"];

// ── Icons ──────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);
const PlusIcon = ({ size }) => <Icon size={size} d="M12 5v14M5 12h14" />;
const TrashIcon = ({ size }) => <Icon size={size} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />;
const CheckIcon = ({ size }) => <Icon size={size} d="M20 6L9 17l-5-5" />;
const XIcon = ({ size }) => <Icon size={size} d="M18 6L6 18M6 6l12 12" />;
const UserIcon = ({ size }) => <Icon size={size} d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />;
const SparkleIcon = ({ size }) => <Icon size={size} d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />;
const EditIcon = ({ size }) => <Icon size={size} d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />;

// ── Styles injected once ───────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display:ital@0;1&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: #faf6f0;
    --cream2: #f3ece0;
    --gold: #b8952a;
    --gold-light: #d4af5a;
    --gold-pale: #f0e4c0;
    --brown: #3b2314;
    --brown-light: #8a6552;
    --text: #2c1f14;
    --text-mid: #6b4f3a;
    --border: #ddd0b8;
    --shadow: rgba(92,61,46,0.12);
    --ayes: #4a7c59;
    --anos: #8b3a3a;
    --apend: #7a6c45;
  }

  body { background: var(--cream); font-family: 'DM Sans', sans-serif; color: var(--text); }

  .app { min-height: 100vh; }

  /* Header */
  .header {
    background: var(--brown);
    padding: 32px 40px 24px;
    text-align: center;
    position: relative;
    border-bottom: 2px solid var(--gold);
  }
  .header::before {
    content: '';
    position: absolute; inset: 0;
    background: repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(184,149,42,0.04) 20px, rgba(184,149,42,0.04) 21px);
    pointer-events: none;
  }
  .header-title {
    font-family: 'DM Serif Display', serif;
    font-size: 44px;
    font-weight: 400;
    color: #ffffff;
    letter-spacing: 0.5px;
    line-height: 1;
    text-shadow: 0 1px 4px rgba(0,0,0,0.3);
  }
  .header-sub {
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #e8d8b0;
    letter-spacing: 4px;
    text-transform: uppercase;
    margin-top: 8px;
  }
  .header-stats {
    display: flex;
    justify-content: center;
    gap: 32px;
    margin-top: 20px;
    flex-wrap: wrap;
  }
  .stat {
    text-align: center;
  }
  .stat-num {
    font-family: 'DM Serif Display', serif;
    font-size: 30px;
    font-weight: 400;
    color: #ffffff;
    display: block;
    line-height: 1;
  }
  .stat-label {
    font-family: 'DM Sans', sans-serif;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: #c8b480;
    margin-top: 4px;
  }
  .stat-divider {
    width: 1px;
    background: rgba(184,149,42,0.35);
    align-self: stretch;
  }

  /* Tabs */
  .tabs {
    display: flex;
    background: var(--brown);
    padding: 0 40px;
    border-bottom: 2px solid var(--gold);
  }
  .tab {
    padding: 14px 28px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: rgba(220,200,160,0.55);
    cursor: pointer;
    border: none;
    background: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;
  }
  .tab:hover { color: #e8d8b0; }
  .tab.active {
    color: #ffffff;
    border-bottom-color: var(--gold-light);
  }

  /* Main */
  .main { padding: 32px 40px; max-width: 1100px; margin: 0 auto; }

  /* Cards */
  .card {
    background: white;
    border: 1px solid var(--border);
    border-radius: 2px;
    box-shadow: 0 2px 16px var(--shadow);
  }
  .card-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .card-title {
    font-family: 'DM Serif Display', serif;
    font-size: 22px;
    font-weight: 400;
    color: var(--brown);
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 18px;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    border: none;
    border-radius: 1px;
    cursor: pointer;
    transition: all 0.15s;
  }
  .btn-gold {
    background: var(--gold);
    color: white;
  }
  .btn-gold:hover { background: var(--brown); }
  .btn-outline {
    background: transparent;
    color: var(--brown);
    border: 1px solid var(--border);
  }
  .btn-outline:hover { border-color: var(--gold); color: var(--gold); }
  .btn-sm { padding: 5px 10px; font-size: 10px; letter-spacing: 1px; }
  .btn-ghost {
    background: transparent;
    color: var(--text-mid);
    border: none;
    padding: 4px 8px;
  }
  .btn-ghost:hover { color: var(--anos); }
  .btn-ai {
    background: linear-gradient(135deg, var(--brown) 0%, var(--gold) 100%);
    color: white;
  }
  .btn-ai:hover { opacity: 0.9; }

  /* Inputs */
  .input, .select {
    width: 100%;
    padding: 9px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    border: 1px solid var(--border);
    border-radius: 1px;
    background: var(--cream);
    color: var(--text);
    transition: border-color 0.15s;
  }
  .input:focus, .select:focus {
    outline: none;
    border-color: var(--gold);
    background: white;
  }
  .label {
    display: block;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--text-mid);
    margin-bottom: 5px;
  }
  .field { margin-bottom: 14px; }

  /* Guest list */
  .guest-form {
    padding: 24px;
    background: var(--cream2);
    border-bottom: 1px solid var(--border);
  }
  .form-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 12px; align-items: end; }
  .form-grid-2 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; align-items: end; }

  .guest-table { width: 100%; border-collapse: collapse; }
  .guest-table th {
    text-align: left;
    padding: 10px 16px;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--text-mid);
    border-bottom: 1px solid var(--border);
    background: var(--cream);
  }
  .guest-table td {
    padding: 11px 16px;
    border-bottom: 1px solid rgba(221,208,184,0.4);
    font-size: 13px;
    vertical-align: middle;
  }
  .guest-table tr:last-child td { border-bottom: none; }
  .guest-table tr:hover td { background: var(--cream2); }

  /* Badges */
  .badge {
    display: inline-block;
    padding: 2px 10px;
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 2px;
    text-transform: uppercase;
    border-radius: 20px;
    white-space: nowrap;
  }
  .badge-a { background: var(--gold-pale); color: var(--gold); border: 1px solid var(--gold-light); }
  .badge-b { background: #e8e4f0; color: #7a5ea0; border: 1px solid #c4b8e0; }
  .badge-yes { background: #e8f5ec; color: var(--ayes); border: 1px solid #a8d4b4; }
  .badge-no { background: #f5e8e8; color: var(--anos); border: 1px solid #e0b4b4; }
  .badge-pending { background: #f5f1e8; color: var(--apend); border: 1px solid #d4c88a; }

  /* Filters */
  .filters {
    padding: 14px 24px;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
    border-bottom: 1px solid var(--border);
    background: var(--cream);
  }
  .filter-btn {
    padding: 4px 14px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 2px;
    text-transform: uppercase;
    border: 1px solid var(--border);
    border-radius: 20px;
    background: white;
    color: var(--text-mid);
    cursor: pointer;
    transition: all 0.15s;
  }
  .filter-btn.active, .filter-btn:hover {
    background: var(--brown);
    color: var(--gold-pale);
    border-color: var(--brown);
  }
  .search-input {
    padding: 6px 12px;
    font-size: 12px;
    border: 1px solid var(--border);
    border-radius: 20px;
    width: 180px;
    background: white;
    font-family: 'DM Sans', sans-serif;
    margin-left: auto;
  }
  .search-input:focus { outline: none; border-color: var(--gold); }

  /* RSVP section */
  .rsvp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; padding: 24px; }
  .rsvp-card {
    border: 1px solid var(--border);
    border-radius: 2px;
    padding: 16px;
    background: white;
    transition: box-shadow 0.15s;
  }
  .rsvp-card:hover { box-shadow: 0 4px 16px var(--shadow); }
  .rsvp-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--brown);
    margin-bottom: 4px;
  }
  .rsvp-meta { font-size: 11px; color: var(--text-mid); margin-bottom: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
  .rsvp-actions { display: flex; gap: 6px; flex-wrap: wrap; }
  .rsvp-btn {
    flex: 1;
    padding: 7px 10px;
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    border-radius: 1px;
    cursor: pointer;
    border: 1px solid;
    transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .rsvp-yes { background: var(--ayes); color: white; border-color: var(--ayes); }
  .rsvp-no { background: var(--anos); color: white; border-color: var(--anos); }
  .rsvp-pending { background: transparent; color: var(--apend); border-color: var(--apend); }
  .rsvp-yes-out { background: transparent; color: var(--ayes); border-color: var(--ayes); }
  .rsvp-no-out { background: transparent; color: var(--anos); border-color: var(--anos); }

  /* Seating */
  .seating-layout { display: grid; grid-template-columns: 280px 1fr; gap: 20px; padding: 20px; }
  .unseated-panel { background: var(--cream2); border: 1px solid var(--border); border-radius: 2px; padding: 16px; }
  .unseated-title {
    font-family: 'DM Serif Display', serif;
    font-size: 18px;
    font-weight: 400;
    color: var(--brown);
    margin-bottom: 12px;
  }
  .unseated-guest {
    padding: 8px 12px;
    background: white;
    border: 1px solid var(--border);
    border-radius: 1px;
    margin-bottom: 6px;
    font-size: 13px;
    cursor: grab;
    user-select: none;
    transition: box-shadow 0.1s, transform 0.1s;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .unseated-guest:hover { box-shadow: 0 2px 8px var(--shadow); transform: translateY(-1px); }
  .unseated-guest.dragging { opacity: 0.4; }
  .tables-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; }
  .table-card {
    border: 2px solid var(--border);
    border-radius: 4px;
    background: white;
    min-height: 120px;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .table-card.over { border-color: var(--gold); box-shadow: 0 0 0 3px var(--gold-pale); }
  .table-card.full { border-color: #e0b4b4; background: #fff8f8; }
  .table-header {
    padding: 10px 14px 8px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .table-name {
    font-family: 'DM Serif Display', serif;
    font-size: 16px;
    font-weight: 400;
    color: var(--brown);
  }
  .table-count { font-size: 11px; color: var(--text-mid); }
  .table-body { padding: 8px; min-height: 60px; }
  .seated-guest {
    padding: 5px 8px;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 1px;
    margin-bottom: 3px;
  }
  .seated-guest:hover { background: var(--cream2); }
  .remove-seat {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-mid);
    opacity: 0;
    padding: 0 2px;
    font-size: 14px;
    line-height: 1;
    transition: opacity 0.1s;
  }
  .seated-guest:hover .remove-seat { opacity: 1; }
  .drop-hint {
    text-align: center;
    font-size: 11px;
    color: var(--border);
    letter-spacing: 1px;
    padding: 12px 8px;
  }

  /* AI panel */
  .ai-result {
    background: var(--cream2);
    border: 1px solid var(--gold-pale);
    border-radius: 2px;
    padding: 16px 20px;
    margin: 0 20px 20px;
    font-size: 13px;
    line-height: 1.7;
    color: var(--text);
    white-space: pre-wrap;
  }
  .ai-loading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--gold);
    font-size: 13px;
    padding: 16px 20px;
    margin: 0 20px 20px;
  }
  .spinner {
    width: 16px; height: 16px;
    border: 2px solid var(--gold-pale);
    border-top-color: var(--gold);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* Summary bar */
  .summary-bar {
    display: flex;
    gap: 20px;
    padding: 16px 24px;
    background: var(--cream2);
    border-bottom: 1px solid var(--border);
    flex-wrap: wrap;
  }
  .summary-item { font-size: 12px; color: var(--text-mid); }
  .summary-item strong { color: var(--brown); font-weight: 500; }

  /* Empty state */
  .empty {
    text-align: center;
    padding: 48px 24px;
    color: var(--text-mid);
  }
  .empty-icon { font-size: 40px; margin-bottom: 12px; }
  .empty-text { font-family: 'DM Serif Display', serif; font-size: 20px; font-weight: 300; color: var(--brown); }
  .empty-sub { font-size: 12px; margin-top: 6px; opacity: 0.6; }

  /* Modal overlay */
  .modal-overlay {
    position: fixed; inset: 0;
    background: rgba(44,31,20,0.5);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
  }
  .modal {
    background: white;
    border: 1px solid var(--border);
    border-radius: 2px;
    padding: 28px;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 20px 60px rgba(44,31,20,0.3);
  }
  .modal-title {
    font-family: 'DM Serif Display', serif;
    font-size: 26px;
    font-weight: 400;
    color: var(--brown);
    margin-bottom: 20px;
  }
  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }

  @media (max-width: 700px) {
    .main { padding: 16px; }
    .header { padding: 20px 16px; }
    .tabs { padding: 0 16px; overflow-x: auto; }
    .form-grid, .form-grid-2 { grid-template-columns: 1fr 1fr; }
    .seating-layout { grid-template-columns: 1fr; }
    .header-title { font-size: 28px; }
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────
function uid(state) { return state.nextId; }

function getStats(state) {
  const aList = state.guests.filter(g => g.list === "A");
  const attending = aList.filter(g => g.rsvp === "yes").reduce((sum, g) => sum + 1 + (g.partnerName ? 1 : 0), 0);
  const declined = aList.filter(g => g.rsvp === "no").length;
  const pending = aList.filter(g => g.rsvp === "pending" || !g.rsvp).length;
  const seated = state.tables.reduce((sum, t) => sum + t.guests.reduce((s, id) => {
    const g = state.guests.find(x => x.id === id);
    return s + (g ? (g.partnerName ? 2 : 1) : 0);
  }, 0), 0);
  const total = aList.reduce((sum, g) => sum + 1 + (g.partnerName ? 1 : 0), 0);
  return { attending, declined, pending, seated, total };
}

// ── CSV helpers ────────────────────────────────────────────────────────────
const CSV_HEADERS = ["name","partnerName","list","group","dietary","partnerDietary","rsvp","address","email","notes"];

function guestsToCSV(guests) {
  const rows = [CSV_HEADERS.join(",")];
  guests.forEach(g => {
    rows.push(CSV_HEADERS.map(h => `"${(g[h] || "").toString().replace(/"/g, '""')}"`).join(","));
  });
  return rows.join("\n");
}

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text.trim().split("\n");
  const headers = lines[0].split(",").map(h => h.trim().replace(/^"|"$/g, ""));
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === "," && !inQ) { vals.push(cur); cur = ""; }
      else { cur += line[i]; }
    }
    vals.push(cur);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || "").trim(); });
    return obj;
  });
}

// ── Sort icon ──────────────────────────────────────────────────────────────
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.25, fontSize: 10, marginLeft: 3 }}>⇅</span>;
  return <span style={{ fontSize: 10, marginLeft: 3, color: "var(--gold)" }}>{sortDir === "asc" ? "↑" : "↓"}</span>;
}

// ── GuestList Tab ──────────────────────────────────────────────────────────
function GuestList({ state, setState }) {
  const [form, setForm] = useState({ name: "", partnerName: "", list: "A", group: "Friends", dietary: "None", partnerDietary: "None", notes: "", address: "", email: "" });
  const [listFilter, setListFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editGuest, setEditGuest] = useState(null);
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [importing, setImporting] = useState(false);
  const fileRef = useState(null);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const add = async () => {
    if (!form.name.trim()) return;
    const newGuest = { ...form, id: state.nextId, rsvp: "pending", invited: true };
    await upsertGuest(newGuest);
    setState(prev => ({ ...prev, guests: [...prev.guests, newGuest], nextId: prev.nextId + 1 }));
    setForm({ name: "", partnerName: "", list: "A", group: "Friends", dietary: "None", partnerDietary: "None", notes: "", address: "", email: "" });
  };

  const remove = async (id) => {
    await deleteGuest(id);
    setState(prev => {
      const tables = prev.tables.map(t => ({ ...t, guests: t.guests.filter(gid => gid !== id) }));
      tables.forEach(t => upsertTable(t));
      return { ...prev, guests: prev.guests.filter(g => g.id !== id), tables };
    });
  };

  const saveEdit = async () => {
    await upsertGuest(editGuest);
    setState(prev => ({ ...prev, guests: prev.guests.map(g => g.id === editGuest.id ? editGuest : g) }));
    setEditGuest(null);
  };

  const handleExport = () => {
    downloadCSV(guestsToCSV(state.guests), "wedding-guests.csv");
  };

  const handleExportTemplate = () => {
    downloadCSV(CSV_HEADERS.join(",") + "\n" + '"Jane Smith","John Smith","A","Friends","None","None","pending","123 Main St, San Francisco CA 94101","jane@email.com","Childhood friend"', "guest-import-template.csv");
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    const text = await file.text();
    try {
      const parsed = parseCSV(text);
      // Delete all existing guests
      await Promise.all(state.guests.map(g => deleteGuest(g.id)));
      // Insert new guests
      let nextId = 1;
      const newGuests = parsed.map((row, i) => ({
        id: nextId + i,
        name: row.name || "",
        partnerName: row.partnerName || "",
        list: row.list === "B" ? "B" : "A",
        group: GROUPS.includes(row.group) ? row.group : "Friends",
        dietary: DIETARY.includes(row.dietary) ? row.dietary : "None",
        partnerDietary: DIETARY.includes(row.partnerDietary) ? row.partnerDietary : "None",
        rsvp: ["yes","no","pending"].includes(row.rsvp) ? row.rsvp : "pending",
        address: row.address || "",
        email: row.email || "",
        notes: row.notes || "",
        invited: true,
      }));
      await Promise.all(newGuests.map(g => upsertGuest(g)));
      setState(prev => ({ ...prev, guests: newGuests, nextId: newGuests.length + 1 }));
    } catch (err) {
      alert("Import failed — please check your CSV matches the template format.");
    }
    setImporting(false);
    e.target.value = "";
  };

  const sortedFiltered = state.guests
    .filter(g => {
      const matchList = listFilter === "all" || g.list === listFilter;
      const matchGroup = groupFilter === "all" || g.group === groupFilter;
      const matchSearch = g.name.toLowerCase().includes(search.toLowerCase()) ||
        (g.partnerName || "").toLowerCase().includes(search.toLowerCase()) ||
        (g.group || "").toLowerCase().includes(search.toLowerCase());
      return matchList && matchGroup && matchSearch;
    })
    .sort((a, b) => {
      let va = "", vb = "";
      if (sortCol === "name") { va = a.name; vb = b.name; }
      else if (sortCol === "partner") { va = a.partnerName || ""; vb = b.partnerName || ""; }
      else if (sortCol === "list") { va = a.list; vb = b.list; }
      else if (sortCol === "group") { va = a.group; vb = b.group; }
      else if (sortCol === "rsvp") { va = a.rsvp || "pending"; vb = b.rsvp || "pending"; }
      else if (sortCol === "dietary") { va = a.dietary; vb = b.dietary; }
      const cmp = va.localeCompare(vb);
      return sortDir === "asc" ? cmp : -cmp;
    });

  const thStyle = { cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };

  return (
    <div className="card">
      <div className="guest-form">
        <div className="form-grid">
          <div className="field">
            <label className="label">Guest Name</label>
            <input className="input" placeholder="e.g. Jane Smith" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} onKeyDown={e => e.key === "Enter" && add()} />
          </div>
          <div className="field">
            <label className="label">Partner Name <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></label>
            <input className="input" placeholder="e.g. John Smith" value={form.partnerName} onChange={e => setForm(f => ({ ...f, partnerName: e.target.value }))} onKeyDown={e => e.key === "Enter" && add()} />
          </div>
          <div className="field">
            <label className="label">List</label>
            <select className="select" value={form.list} onChange={e => setForm(f => ({ ...f, list: e.target.value }))}>
              <option value="A">A-List (Priority)</option>
              <option value="B">B-List (Backup)</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Group</label>
            <select className="select" value={form.group} onChange={e => setForm(f => ({ ...f, group: e.target.value }))}>
              {GROUPS.map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
        </div>
        <div style={{display:"flex", gap:12, alignItems:"flex-end", flexWrap:"wrap"}}>
          <div className="field" style={{flex:1, minWidth:120}}>
            <label className="label">Guest Dietary</label>
            <select className="select" value={form.dietary} onChange={e => setForm(f => ({ ...f, dietary: e.target.value }))}>
              {DIETARY.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>
          {form.partnerName && (
            <div className="field" style={{flex:1, minWidth:120}}>
              <label className="label">Partner Dietary</label>
              <select className="select" value={form.partnerDietary} onChange={e => setForm(f => ({ ...f, partnerDietary: e.target.value }))}>
                {DIETARY.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          )}
          <div className="field" style={{flex:2, minWidth:160}}>
            <label className="label">Notes</label>
            <input className="input" placeholder="Optional notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="field">
            <button className="btn btn-gold" onClick={add}><PlusIcon size={14} /> Add Guest</button>
          </div>
        </div>
      </div>

      <div className="filters">
        {["all", "A", "B"].map(f => (
          <button key={f} className={`filter-btn ${listFilter === f ? "active" : ""}`} onClick={() => setListFilter(f)}>
            {f === "all" ? "All" : `${f}-List`}
          </button>
        ))}
        <div style={{ width: 1, background: "var(--border)", alignSelf: "stretch", margin: "0 4px" }} />
        <button className={`filter-btn ${groupFilter === "all" ? "active" : ""}`} onClick={() => setGroupFilter("all")}>All Groups</button>
        {GROUPS.map(g => (
          <button key={g} className={`filter-btn ${groupFilter === g ? "active" : ""}`} onClick={() => setGroupFilter(g)}>{g}</button>
        ))}
        <span style={{ fontSize: 12, color: "var(--text-mid)", marginLeft: 4 }}>{sortedFiltered.length} guests</span>
        <input className="search-input" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Import / Export bar */}
      <div style={{ padding: "10px 24px", background: "var(--cream)", borderBottom: "1px solid var(--border)", display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--text-mid)", letterSpacing: 1, textTransform: "uppercase", fontWeight: 500 }}>CSV:</span>
        <button className="btn btn-outline btn-sm" onClick={handleExport}>↓ Export Guests</button>
        <button className="btn btn-outline btn-sm" onClick={handleExportTemplate}>↓ Download Template</button>
        <label className={`btn btn-outline btn-sm ${importing ? "" : ""}`} style={{ cursor: "pointer" }}>
          {importing ? "Importing…" : "↑ Import CSV"}
          <input type="file" accept=".csv" style={{ display: "none" }} onChange={handleImport} />
        </label>
        <span style={{ fontSize: 11, color: "var(--text-mid)", marginLeft: 4 }}>⚠ Import replaces all guests</span>
      </div>

      {sortedFiltered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">💌</div>
          <div className="empty-text">No guests yet</div>
          <div className="empty-sub">Start building your guest list above</div>
        </div>
      ) : (
        <table className="guest-table">
          <thead>
            <tr>
              <th style={thStyle} onClick={() => handleSort("name")}>Name <SortIcon col="name" sortCol={sortCol} sortDir={sortDir} /></th>
              <th style={thStyle} onClick={() => handleSort("partner")}>Partner <SortIcon col="partner" sortCol={sortCol} sortDir={sortDir} /></th>
              <th style={thStyle} onClick={() => handleSort("list")}>List <SortIcon col="list" sortCol={sortCol} sortDir={sortDir} /></th>
              <th style={thStyle} onClick={() => handleSort("group")}>Group <SortIcon col="group" sortCol={sortCol} sortDir={sortDir} /></th>
              <th style={thStyle} onClick={() => handleSort("rsvp")}>RSVP <SortIcon col="rsvp" sortCol={sortCol} sortDir={sortDir} /></th>
              <th style={thStyle} onClick={() => handleSort("dietary")}>Dietary <SortIcon col="dietary" sortCol={sortCol} sortDir={sortDir} /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.map(g => (
              <tr key={g.id}>
                <td style={{ fontWeight: 500, fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>{g.name}</td>
                <td style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "var(--text-mid)" }}>{g.partnerName || <span style={{opacity:0.3}}>—</span>}</td>
                <td><span className={`badge badge-${g.list.toLowerCase()}`}>{g.list}-List</span></td>
                <td style={{ fontSize: 12, color: "var(--text-mid)" }}>{g.group}</td>
                <td><span className={`badge badge-${g.rsvp || "pending"}`}>{g.rsvp || "pending"}</span></td>
                <td style={{ fontSize: 12, color: "var(--text-mid)" }}>
                  {g.dietary && g.dietary !== "None" ? g.dietary : ""}
                  {g.partnerName && g.partnerDietary && g.partnerDietary !== "None" ? (g.dietary && g.dietary !== "None" ? ` / ${g.partnerDietary}` : g.partnerDietary) : ""}
                  {(!g.dietary || g.dietary === "None") && (!g.partnerDietary || g.partnerDietary === "None") ? "—" : ""}
                </td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditGuest({ ...g })}><EditIcon size={13} /></button>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(g.id)}><TrashIcon size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editGuest && (
        <div className="modal-overlay" onClick={() => setEditGuest(null)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-title">Edit Guest</div>
            <div className="form-grid-2">
              <div className="field">
                <label className="label">Name</label>
                <input className="input" value={editGuest.name} onChange={e => setEditGuest(g => ({ ...g, name: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">Partner Name <span style={{fontWeight:300,opacity:0.6}}>(optional)</span></label>
                <input className="input" placeholder="Leave blank if solo" value={editGuest.partnerName || ""} onChange={e => setEditGuest(g => ({ ...g, partnerName: e.target.value }))} />
              </div>
              <div className="field">
                <label className="label">List</label>
                <select className="select" value={editGuest.list} onChange={e => setEditGuest(g => ({ ...g, list: e.target.value }))}>
                  <option value="A">A-List</option><option value="B">B-List</option>
                </select>
              </div>
              <div className="field">
                <label className="label">Group</label>
                <select className="select" value={editGuest.group} onChange={e => setEditGuest(g => ({ ...g, group: e.target.value }))}>
                  {GROUPS.map(g => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Guest Dietary</label>
                <select className="select" value={editGuest.dietary} onChange={e => setEditGuest(g => ({ ...g, dietary: e.target.value }))}>
                  {DIETARY.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Partner Dietary</label>
                <select className="select" value={editGuest.partnerDietary || "None"} onChange={e => setEditGuest(g => ({ ...g, partnerDietary: e.target.value }))} disabled={!editGuest.partnerName}>
                  {DIETARY.map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">RSVP</label>
                <select className="select" value={editGuest.rsvp || "pending"} onChange={e => setEditGuest(g => ({ ...g, rsvp: e.target.value }))}>
                  <option value="pending">Pending</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label className="label">Email Address</label>
              <input className="input" type="email" placeholder="jane@email.com" value={editGuest.email || ""} onChange={e => setEditGuest(g => ({ ...g, email: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">Mailing Address</label>
              <input className="input" placeholder="123 Main St, San Francisco CA 94101" value={editGuest.address || ""} onChange={e => setEditGuest(g => ({ ...g, address: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">Notes</label>
              <input className="input" placeholder="Any notes..." value={editGuest.notes || ""} onChange={e => setEditGuest(g => ({ ...g, notes: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setEditGuest(null)}>Cancel</button>
              <button className="btn btn-gold" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── RSVP Tab ───────────────────────────────────────────────────────────────
function RSVPTracker({ state, setState }) {
  const [filter, setFilter] = useState("all");

  const setRsvp = async (id, rsvp) => {
    const guest = state.guests.find(g => g.id === id);
    await upsertGuest({ ...guest, rsvp });
    setState(prev => ({ ...prev, guests: prev.guests.map(g => g.id === id ? { ...g, rsvp } : g) }));
  };

  const invited = state.guests.filter(g => g.list === "A");
  const normaliseRsvp = (g) => g.rsvp === "yes" || g.rsvp === "no" ? g.rsvp : "pending";
  const filtered = invited.filter(g => filter === "all" || normaliseRsvp(g) === filter);

  const stats = {
    yes: invited.filter(g => g.rsvp === "yes").length,
    no: invited.filter(g => g.rsvp === "no").length,
    pending: invited.filter(g => !g.rsvp || g.rsvp === "pending").length,
  };
  const attending = invited.filter(g => g.rsvp === "yes").reduce((sum, g) => sum + 1 + (g.partnerName ? 1 : 0), 0);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">RSVP Tracker</span>
        <span style={{ fontSize: 12, color: "var(--text-mid)" }}>A-List guests only</span>
      </div>
      <div className="summary-bar">
        <div className="summary-item">✅ <strong>{stats.yes}</strong> attending</div>
        <div className="summary-item">❌ <strong>{stats.no}</strong> declined</div>
        <div className="summary-item">⏳ <strong>{stats.pending}</strong> pending</div>
        <div className="summary-item" style={{ marginLeft: "auto" }}>🪑 <strong>{attending}</strong> total seats needed</div>
      </div>
      <div className="filters" style={{ borderTop: "none" }}>
        {["all", "yes", "no", "pending"].map(f => (
          <button key={f} className={`filter-btn ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)}>
            {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📬</div>
          <div className="empty-text">No A-List guests yet</div>
          <div className="empty-sub">Add guests in the Guest List tab first</div>
        </div>
      ) : (
        <div className="rsvp-grid">
          {filtered.map(g => (
            <div key={g.id} className="rsvp-card">
              <div className="rsvp-name">
                {g.name}
                {g.partnerName && (
                  <span style={{ display: "block", fontSize: 15, color: "var(--text-mid)", fontWeight: 400, marginTop: 1 }}>
                    & {g.partnerName}
                  </span>
                )}
              </div>
              <div className="rsvp-meta">
                <span>{g.group}</span>
                {g.partnerName && <span>· Couple</span>}
                {g.dietary && g.dietary !== "None" && <span>· {g.name.split(" ")[0]}: {g.dietary}</span>}
                {g.partnerName && g.partnerDietary && g.partnerDietary !== "None" && <span>· {g.partnerName.split(" ")[0]}: {g.partnerDietary}</span>}
                {g.notes && <span>· {g.notes}</span>}
              </div>
              <div className="rsvp-actions">
                <button
                  className={`rsvp-btn ${g.rsvp === "yes" ? "rsvp-yes" : "rsvp-yes-out"}`}
                  onClick={() => setRsvp(g.id, "yes")}
                >✓ Attending</button>
                <button
                  className={`rsvp-btn ${g.rsvp === "no" ? "rsvp-no" : "rsvp-no-out"}`}
                  onClick={() => setRsvp(g.id, "no")}
                >✗ Declined</button>
              </div>
              {(g.rsvp === "pending" || !g.rsvp) && (
                <div style={{ fontSize: 11, color: "var(--apend)", marginTop: 6, textAlign: "center" }}>⏳ Awaiting response</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Seating Tab ────────────────────────────────────────────────────────────
function SeatingChart({ state, setState }) {
  const [dragGuest, setDragGuest] = useState(null);
  const [overTable, setOverTable] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [tableCount, setTableCount] = useState(state.tables.length);

  const attending = state.guests.filter(g => g.rsvp === "yes");
  const seatedIds = new Set(state.tables.flatMap(t => t.guests));
  const unseated = attending.filter(g => !seatedIds.has(g.id));

  // A couple takes 2 seats, solo takes 1
  const seatsTaken = (guests) => guests.reduce((sum, id) => {
    const g = state.guests.find(x => x.id === id);
    return sum + (g ? (g.partnerName ? 2 : 1) : 0);
  }, 0);

  const moveToTable = async (guest, tableId) => {
    const tables = state.tables.map(t => ({
      ...t,
      guests: t.guests.filter(id => id !== guest.id),
    }));
    const target = tables.find(t => t.id === tableId);
    const seats = seatsTaken(target.guests);
    const needed = guest.partnerName ? 2 : 1;
    if (target && seats + needed <= target.capacity) {
      target.guests.push(guest.id);
    }
    await Promise.all(tables.map(t => upsertTable(t)));
    setState(prev => ({ ...prev, tables }));
  };

  const removeFromTable = async (guestId) => {
    const tables = state.tables.map(t => ({ ...t, guests: t.guests.filter(id => id !== guestId) }));
    await Promise.all(tables.map(t => upsertTable(t)));
    setState(prev => ({ ...prev, tables }));
  };

  const updateTableCount = async (n) => {
    const count = Math.max(1, Math.min(30, n));
    setTableCount(count);
    let tables = [...state.tables];
    while (tables.length < count) {
      tables.push({ id: `table-${Date.now()}-${tables.length}`, name: `Table ${tables.length + 1}`, capacity: 8, guests: [] });
    }
    if (tables.length > count) tables = tables.slice(0, count);
    await Promise.all(tables.map(t => upsertTable(t)));
    setState(prev => ({ ...prev, tables }));
  };

  const aiAutoSeat = async () => {
    setAiLoading(true);
    setAiResult(null);
    const guestData = attending.map(g => ({
      name: g.partnerName ? `${g.name} & ${g.partnerName}` : g.name,
      group: g.group,
      seats: g.partnerName ? 2 : 1,
      dietary: [g.dietary, g.partnerDietary].filter(d => d && d !== "None").join(", ") || "None"
    }));
    const tableData = state.tables.map(t => ({ name: t.name, capacity: t.capacity }));
    const prompt = `I have wedding guests to seat at ${state.tables.length} tables. Couples are listed together and take 2 seats.

Guests:
${guestData.map((g, i) => `${i + 1}. ${g.name} (${g.seats} seat${g.seats > 1 ? "s" : ""}, Group: ${g.group}, Dietary: ${g.dietary})`).join('\n')}

Tables:
${tableData.map(t => `- ${t.name}: capacity ${t.capacity} seats`).join('\n')}

Please suggest a seating arrangement. Keep couples together at the same table. Group by group (Family, Friends, Work, etc.). List each table and who should sit there. Be concise.`;

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{ role: "user", content: prompt }]
        })
      });
      const data = await res.json();
      setAiResult(data.content?.[0]?.text || "No suggestion returned.");
    } catch (e) {
      setAiResult("Error reaching AI. Please try again.");
    }
    setAiLoading(false);
  };

  const unseatedSeatCount = unseated.reduce((sum, g) => sum + (g.partnerName ? 2 : 1), 0);

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">Table Seating</span>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label className="label" style={{ marginBottom: 0 }}>Tables:</label>
          <input type="number" className="input" style={{ width: 60, padding: "5px 8px" }} value={tableCount} min={1} max={30}
            onChange={e => updateTableCount(parseInt(e.target.value) || 1)} />
          {attending.length > 0 && (
            <button className="btn btn-ai btn-sm" onClick={aiAutoSeat} disabled={aiLoading}>
              <SparkleIcon size={13} /> AI Suggest
            </button>
          )}
        </div>
      </div>

      {aiLoading && (
        <div className="ai-loading"><div className="spinner" /> Generating seating suggestions…</div>
      )}
      {aiResult && (
        <div className="ai-result">{aiResult}</div>
      )}

      {attending.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">🪑</div>
          <div className="empty-text">No confirmed guests yet</div>
          <div className="empty-sub">Mark guests as attending in the RSVP tab first</div>
        </div>
      ) : (
        <div className="seating-layout">
          <div className="unseated-panel">
            <div className="unseated-title">Unseated ({unseatedSeatCount} seats)</div>
            {unseated.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--ayes)", textAlign: "center", padding: "16px 0" }}>🎉 Everyone is seated!</div>
            ) : (
              unseated.map(g => (
                <div
                  key={g.id}
                  className={`unseated-guest ${dragGuest?.id === g.id ? "dragging" : ""}`}
                  draggable
                  onDragStart={() => setDragGuest(g)}
                  onDragEnd={() => { setDragGuest(null); setOverTable(null); }}
                >
                  <UserIcon size={12} />
                  <span style={{ flex: 1 }}>
                    {g.name}
                    {g.partnerName && <span style={{ display: "block", fontSize: 10, color: "var(--text-mid)" }}>& {g.partnerName}</span>}
                  </span>
                  <span style={{ fontSize: 10, color: "var(--text-mid)" }}>{g.partnerName ? "×2" : "×1"}</span>
                </div>
              ))
            )}
          </div>

          <div className="tables-grid">
            {state.tables.map(table => {
              const seatedGuests = table.guests.map(id => state.guests.find(g => g.id === id)).filter(Boolean);
              const usedSeats = seatsTaken(table.guests);
              const isFull = usedSeats >= table.capacity;
              const wouldFit = dragGuest ? usedSeats + (dragGuest.partnerName ? 2 : 1) <= table.capacity : true;
              return (
                <div
                  key={table.id}
                  className={`table-card ${overTable === table.id ? "over" : ""} ${isFull ? "full" : ""}`}
                  onDragOver={e => { e.preventDefault(); setOverTable(table.id); }}
                  onDragLeave={() => setOverTable(null)}
                  onDrop={() => {
                    if (dragGuest && wouldFit) moveToTable(dragGuest, table.id);
                    setOverTable(null); setDragGuest(null);
                  }}
                >
                  <div className="table-header">
                    <span className="table-name">{table.name}</span>
                    <span className="table-count">{usedSeats}/{table.capacity}</span>
                  </div>
                  <div className="table-body">
                    {seatedGuests.length === 0 && <div className="drop-hint">Drop guests here</div>}
                    {seatedGuests.map(g => (
                      <div key={g.id} className="seated-guest">
                        <span>
                          {g.name}
                          {g.partnerName && <span style={{ fontSize: 11, color: "var(--text-mid)" }}> & {g.partnerName}</span>}
                        </span>
                        <button className="remove-seat" onClick={() => removeFromTable(g.id)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Password Gate ──────────────────────────────────────────────────────────
const PASSWORD = "wedding2026";

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState("");
  const [error, setError] = useState(false);

  const attempt = () => {
    if (input === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setInput("");
      setTimeout(() => setError(false), 2000);
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: "var(--brown)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: "white", padding: "48px 40px", borderRadius: 2,
        width: "100%", maxWidth: 380, textAlign: "center",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        border: "1px solid var(--border)",
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>💍</div>
        <div style={{
          fontFamily: "'DM Serif Display', serif", fontSize: 26,
          color: "var(--brown)", marginBottom: 6,
        }}>Wedding Command Centre</div>
        <div style={{ fontSize: 12, color: "var(--text-mid)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 28 }}>
          Private Access
        </div>
        <input
          className="input"
          type="password"
          placeholder="Enter password"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && attempt()}
          style={{ marginBottom: 12, textAlign: "center", letterSpacing: 3 }}
          autoFocus
        />
        {error && (
          <div style={{ fontSize: 12, color: "var(--anos)", marginBottom: 10 }}>Incorrect password, try again</div>
        )}
        <button className="btn btn-gold" style={{ width: "100%", justifyContent: "center" }} onClick={attempt}>
          Enter
        </button>
      </div>
    </div>
  );
}

// ── App ────────────────────────────────────────────────────────────────────
export default function App() {
  const [state, setState] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("guests");
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("wcc_auth") === "1");

  useEffect(() => {
    if (!unlocked) return;
    loadFromSupabase().then(data => {
      setState(data);
      setLoading(false);
    });
  }, [unlocked]);

  if (!unlocked) {
    return (
      <>
        <style>{CSS}</style>
        <PasswordGate onUnlock={() => { sessionStorage.setItem("wcc_auth", "1"); setUnlocked(true); }} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <style>{CSS}</style>
        <div style={{ minHeight: "100vh", background: "var(--brown)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center", color: "var(--gold-light)" }}>
            <div className="spinner" style={{ margin: "0 auto 16px", width: 32, height: 32, borderWidth: 3 }} />
            <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20 }}>Loading your wedding data…</div>
          </div>
        </div>
      </>
    );
  }

  const stats = getStats(state);

  return (
    <>
      <style>{CSS}</style>
      <div className="app">
        <div className="header">
          <div className="header-title">Wedding Command Centre</div>
          <div className="header-sub">January 2026 · Guest & Seating Manager</div>
          <div className="header-stats">
            <div className="stat"><span className="stat-num">{stats.total}</span><div className="stat-label">Total Guests</div></div>
            <div className="stat-divider" />
            <div className="stat"><span className="stat-num">{stats.attending}</span><div className="stat-label">Attending</div></div>
            <div className="stat-divider" />
            <div className="stat"><span className="stat-num">{stats.pending}</span><div className="stat-label">Pending</div></div>
            <div className="stat-divider" />
            <div className="stat"><span className="stat-num">{stats.seated}</span><div className="stat-label">Seated</div></div>
          </div>
        </div>

        <div className="tabs">
          {[["guests", "Guest List"], ["rsvp", "RSVP Tracker"], ["seating", "Table Seating"]].map(([id, label]) => (
            <button key={id} className={`tab ${tab === id ? "active" : ""}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        <div className="main">
          {tab === "guests" && <GuestList state={state} setState={setState} />}
          {tab === "rsvp" && <RSVPTracker state={state} setState={setState} />}
          {tab === "seating" && <SeatingChart state={state} setState={setState} />}
        </div>
      </div>
    </>
  );
}
